import * as BufferLayout from 'buffer-layout';
import BN from 'bn.js';
import { PublicKey } from '@solana/web3.js';
/**
 * Custom layouts for 64-bit integers as blobs.
 * You can later convert these blobs to BN using little-endian encoding.
 */
const uint64 = (property) => BufferLayout.blob(8, property);
const int64 = (property) => BufferLayout.blob(8, property);
const i32 = (property) => {
    const layout = BufferLayout.blob(4, property);
    layout.decode = (buffer, offset = 0) => {
        return buffer.readInt32LE(offset);
    };
    layout.encode = (num, buffer, offset = 0) => {
        return buffer.writeInt32LE(num, offset);
    };
    layout.span = 4;
    return layout;
};
/**
 * Layout for a public key (32 bytes)
 */
const publicKeyLayout = (property) => BufferLayout.blob(32, property);
/**
 * PriceFeedMessage layout as defined in the IDL.
 * Fields:
 * - feedId: [u8; 32]
 * - price: i64 (8 bytes)
 * - conf: u64 (8 bytes)
 * - exponent: i32 (4 bytes)
 * - publishTime: i64 (8 bytes)
 * - prevPublishTime: i64 (8 bytes)
 * - emaPrice: i64 (8 bytes)
 * - emaConf: u64 (8 bytes)
 */
const PriceFeedMessageLayout = BufferLayout.struct([
    BufferLayout.blob(32, 'feedId'),
    int64('price'),
    uint64('conf'),
    i32('exponent'),
    int64('publishTime'),
    int64('prevPublishTime'),
    int64('emaPrice'),
    uint64('emaConf'),
]);
/**
 * VerificationLevel is defined as an enum with two variants:
 * - Partial { numSignatures: u8 }  (we use tag 0)
 * - Full                         (we use tag 1)
 *
 * Because buffer-layout doesn’t directly support unions, we decode it manually.
 */
function decodeVerificationLevel(buffer, offset) {
    const tag = buffer.readUInt8(offset);
    if (tag === 0) {
        const numSignatures = buffer.readUInt8(offset + 1);
        return { value: { Partial: { numSignatures } }, length: 2 };
    }
    else if (tag === 1) {
        return { value: 'Full', length: 1 };
    }
    else {
        throw new Error(`Unknown VerificationLevel tag: ${tag}`);
    }
}
/**
 * PriceUpdateV2 layout consists of:
 * - discriminator [u8; 8] (8 bytes)
 * - writeAuthority: publicKey (32 bytes)
 * - verificationLevel: (union; manually decoded)
 * - priceMessage: PriceFeedMessage (defined above)
 * - postedSlot: u64 (8 bytes)
 *
 * Because of the union field, we split the decoding manually.
 */
export function decodePriceUpdateV2(buffer) {
    let offset = 0;
    // 0. Disciminator
    // const discriminator = buffer.slice(offset, offset + 8);
    offset += 8;
    // 1. Decode writeAuthority (32 bytes)
    const writeAuthorityBytes = buffer.slice(offset, offset + 32);
    const writeAuthority = new PublicKey(writeAuthorityBytes);
    offset += 32;
    // 2. Decode verificationLevel manually (variable length)
    const { value: verificationLevel, length: verifLength } = decodeVerificationLevel(buffer, offset);
    offset += verifLength;
    // 3. Decode priceMessage using the layout.
    //    PriceFeedMessageLayout.span gives the number of bytes the layout occupies.
    const priceMessageBytes = buffer.slice(offset, offset + PriceFeedMessageLayout.span);
    const rawPriceMessage = PriceFeedMessageLayout.decode(priceMessageBytes);
    offset += PriceFeedMessageLayout.span;
    // 4. Decode postedSlot (8 bytes)
    const postedSlotBytes = buffer.slice(offset, offset + 8);
    const postedSlot = new BN(postedSlotBytes, 'le');
    offset += 8;
    // Convert the raw price message fields:
    const priceMessage = {
        feedId: rawPriceMessage.feedId, // Buffer (32 bytes)
        price: new BN(rawPriceMessage.price, 'le'),
        conf: new BN(rawPriceMessage.conf, 'le'),
        exponent: rawPriceMessage.exponent,
        publishTime: new BN(rawPriceMessage.publishTime, 'le'),
        prevPublishTime: new BN(rawPriceMessage.prevPublishTime, 'le'),
        emaPrice: new BN(rawPriceMessage.emaPrice, 'le'),
        emaConf: new BN(rawPriceMessage.emaConf, 'le'),
    };
    return {
        writeAuthority, // PublicKey
        verificationLevel, // Either "Full" or { Partial: { numSignatures: number } }
        priceMessage,
        postedSlot, // BN instance
    };
}
/** Example usage */
// Assume you have a Buffer `accountData` that contains the raw bytes of a PriceUpdateV2 account.
// const accountData: Buffer = ...;
// const decodedPriceUpdateV2 = decodePriceUpdateV2(accountData);
// console.log(decodedPriceUpdateV2);
export const getPriceUpdateV2 = async (connection, priceUpdateAccount) => {
    const accountInfo = await connection.getAccountInfo(new PublicKey(priceUpdateAccount));
    return decodePriceUpdateV2(accountInfo.data);
};
